/**
 * LongPostCard Component
 * 
 * A reusable card component that displays individual long post information including:
 * - Post title, thumbnail, and metadata
 * - Publication status (Ready/Draft) with visual indicators
 * - Author, source, and language information
 * - Progress indicators and statistics (chunks count, reading time)
 * - Category and tags display
 * - Click handler for navigation to chunk management
 * - Menu button for additional actions (edit, delete)
 * 
 * Used in: LongPostList component
 */
import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Avatar,
    LinearProgress
} from '@mui/material';
import { MoreVert, CheckCircle, Warning } from '@mui/icons-material';
import PropTypes from 'prop-types';

const LongPostCard = ({ post, onCardClick, onMenuClick }) => {
    if (!post) {
        return null;
    }

    return (
        <Paper 
            onClick={() => onCardClick(post)}
            sx={{ 
                p: 0,
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                }
            }}
        >
            <Box sx={{ p: 2 }}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                    {/* Thumbnail */}
                    <Avatar 
                        src={post.thumbUrl} 
                        variant="square" 
                        sx={{ width: 56, height: 56, flexShrink: 0 }}
                    />
                    
                    {/* Main Content */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Box display="flex" alignItems="center" gap={1} sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }} noWrap>
                                    {post.title}
                                </Typography>
                                <Tooltip title={post.isReadyToServe ? "Ready to serve to users" : "Not ready to serve"}>
                                    {post.isReadyToServe ? (
                                        <CheckCircle color="success" fontSize="small" />
                                    ) : (
                                        <Warning color="warning" fontSize="small" />
                                    )}
                                </Tooltip>
                            </Box>
                            
                            <Tooltip title="More options">
                                <IconButton
                                    size="small"
                                    onClick={(e) => onMenuClick(e, post)}
                                    sx={{ ml: 1, flexShrink: 0 }}
                                >
                                    <MoreVert />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        
                        {/* Author, Language, and Level */}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {post.author && `by ${post.author}`}
                            {post.author && (post.language?.name || post.level?.name) && ' • '}
                            {post.language?.name}
                            {post.language?.name && post.level?.name && ' • '}
                            {post.level?.name && `${post.level.name}`}
                            {post.isReadyToServe ? (
                                <Chip 
                                    label="Ready" 
                                    size="small" 
                                    color="success" 
                                    variant="outlined"
                                    sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                                />
                            ) : (
                                <Chip 
                                    label="Draft" 
                                    size="small" 
                                    color="warning" 
                                    variant="outlined"
                                    sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                                />
                            )}
                        </Typography>
                        
                        {/* Source */}
                        {post.sourceTitle && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Source: {post.sourceTitle}
                            </Typography>
                        )}
                        
                        {/* Summary */}
                        {post.summary && (
                            <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ 
                                    mb: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                }}
                            >
                                {post.summary}
                            </Typography>
                        )}
                        
                        {/* Progress Bar - if there's translation data */}
                        {post.translationProgress && (
                            <Box sx={{ mb: 1 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Translation Progress
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {post.translationProgress.averageProgress}% 
                                        ({post.translationProgress.totalTranslations} {post.translationProgress.totalTranslations === 1 ? 'translation' : 'translations'})
                                    </Typography>
                                </Box>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={post.translationProgress.averageProgress}
                                    sx={{ 
                                        height: 4, 
                                        borderRadius: 2,
                                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 2,
                                            backgroundColor: post.translationProgress.averageProgress === 0 
                                                ? '#f44336' 
                                                : post.translationProgress.averageProgress < 50 
                                                    ? '#ff9800' 
                                                    : post.translationProgress.averageProgress < 100 
                                                        ? '#2196f3' 
                                                        : '#4caf50'
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
                
                {/* Bottom Section: Stats and Tags */}
                <Box sx={{ mt: 2 }}>
                    {/* Stats Chips */}
                    <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                        <Chip label={post.category} size="small" color="primary" />
                        {post.level?.name && (
                            <Chip 
                                label={post.level.name}
                                size="small"
                                color={
                                    post.level.name === 'Beginner' ? 'success' :
                                    post.level.name === 'Intermediate' ? 'warning' :
                                    post.level.name === 'Advanced' ? 'error' : 'default'
                                }
                                variant="outlined"
                            />
                        )}
                        <Chip
                            label={`${post.chunks?.length || 0} chunks`}
                            size="small"
                            variant="outlined"
                        />
                        <Chip
                            label={`${post.estimatedReadingTime || 0} min read`}
                            size="small"
                            variant="outlined"
                        />
                    </Box>
                    
                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                            {post.tags.slice(0, 4).map((tag, index) => (
                                <Chip
                                    key={index}
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                    sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem', height: '20px' }}
                                />
                            ))}
                            {post.tags.length > 4 && (
                                <Chip
                                    label={`+${post.tags.length - 4} more`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem', height: '20px', color: 'text.secondary' }}
                                />
                            )}
                        </Box>
                    )}
                    
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', mt: 1 }}>
                        Click to manage chunks
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
};

LongPostCard.propTypes = {
    post: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        summary: PropTypes.string,
        category: PropTypes.string,
        chunks: PropTypes.array,
        estimatedReadingTime: PropTypes.number,
        isReadyToServe: PropTypes.bool,
        thumbUrl: PropTypes.string,
        author: PropTypes.string,
        sourceTitle: PropTypes.string,
        language: PropTypes.shape({
            name: PropTypes.string
        }),
        level: PropTypes.shape({
            _id: PropTypes.string,
            name: PropTypes.string
        }),
        tags: PropTypes.arrayOf(PropTypes.string),
        translationProgress: PropTypes.shape({
            averageProgress: PropTypes.number,
            totalTranslations: PropTypes.number
        })
    }).isRequired,
    onCardClick: PropTypes.func.isRequired,
    onMenuClick: PropTypes.func.isRequired
};

export default LongPostCard; 